import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MapPin, Edit, Trash2, Home, Building2, Star, ArrowLeft } from 'lucide-react';
import api from '../../api/axios';
import Header from '../common/Header';
import Footer from '../common/Footer';
import Skeleton from 'react-loading-skeleton';

export default function AddressManager() {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    title: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    pincode: '',
    landmark: '',
    is_default: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const response = await api.get('/addresses/');

      // Handle response as array directly
      if (Array.isArray(response.data)) {
        setAddresses(response.data);
      } else if (response.data?.success) {
        setAddresses(response.data.data || response.data.addresses || []);
      } else if (response.data?.data) {
        setAddresses(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        setAddresses([]);
        setError('Failed to load addresses');
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
      setAddresses([]); // Ensure addresses is always an array
      setError('Failed to load addresses');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      title: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      pincode: '',
      landmark: '',
      is_default: false
    });
    setEditingAddress(null);
    setShowForm(false);
    setError('');
  };

  const handleEdit = (address) => {
    setFormData({
      name: address.name || '',
      phone: address.phone || '',
      title: address.title || address.address_type || '',
      address_line_1: address.address_line_1 || address.address || '',
      address_line_2: address.address_line_2 || '',
      city: address.city || '',
      state: address.state || '',
      pincode: address.pincode || '',
      landmark: address.landmark || '',
      is_default: address.is_default || false
    });
    setEditingAddress(address);
    setShowForm(true);
  };

  const handleDelete = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    try {
      const response = await api.delete(`/addresses/${addressId}/delete/`);

      if (response.data.message || response.status === 200) {
        setAddresses((addresses || []).filter(addr => addr.id !== addressId));
      } else {
        setError('Failed to delete address');
      }
    } catch (error) {
      console.error('Error deleting address:', error);
      setError('Failed to delete address');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name.trim() || !formData.phone.trim() ||
        !formData.address_line_1.trim() || !formData.city.trim() ||
        !formData.state.trim() || !formData.pincode.trim()) {
      setError('Please fill all required fields');
      return;
    }

    // Validate phone number
    if (!/^[6-9]\d{9}$/.test(formData.phone.trim())) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Construct address string for backend
      const addressString = [
        formData.address_line_1,
        formData.address_line_2,
        formData.city,
        formData.state,
        formData.pincode,
        formData.landmark
      ].filter(Boolean).join(', ');

      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        address: addressString,
        ...formData
      };

      let response;
      if (editingAddress) {
        response = await api.put(`/addresses/${editingAddress.id}/edit/`, payload);
      } else {
        response = await api.post('/addresses/add/', payload);
      }

      if (response.data || response.status === 200 || response.status === 201) {
        await fetchAddresses(); // Refresh the list
        resetForm();
      } else {
        setError('Failed to save address');
      }
    } catch (error) {
      console.error('Error saving address:', error);
      if (error.response?.data) {
        // Handle field-specific errors
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          const errorMessages = Object.entries(errorData)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('\n');
          setError(errorMessages);
        } else {
          setError(errorData.error || 'Failed to save address');
        }
      } else {
        setError('Failed to save address');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const getAddressIcon = (title) => {
    const lowerTitle = (title || '').toLowerCase();
    if (lowerTitle.includes('home') || lowerTitle.includes('house')) {
      return <Home className="w-5 h-5 text-blue-500" />;
    } else if (lowerTitle.includes('office') || lowerTitle.includes('work')) {
      return <Building2 className="w-5 h-5 text-green-500" />;
    }
    return <MapPin className="w-5 h-5 text-gray-500" />;
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Back Button */}
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold transition-colors mb-6"
          >
            <ArrowLeft size={20} />
            Back to Profile
          </button>

          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Addresses</h1>
              <p className="text-gray-600 mt-1">Manage your delivery addresses</p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>Add Address</span>
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Address Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-screen overflow-y-auto">
                <div className="p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">
                    {editingAddress ? 'Edit Address' : 'Add New Address'}
                  </h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter your full name"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="10-digit mobile number"
                        required
                        maxLength="10"
                        pattern="[6-9][0-9]{9}"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address Title
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="e.g., Home, Office (Optional)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Address Line 1 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address Line 1 *
                      </label>
                      <input
                        type="text"
                        name="address_line_1"
                        value={formData.address_line_1}
                        onChange={handleInputChange}
                        placeholder="House/Flat No, Street"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Address Line 2 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address Line 2
                      </label>
                      <input
                        type="text"
                        name="address_line_2"
                        value={formData.address_line_2}
                        onChange={handleInputChange}
                        placeholder="Area, Locality (Optional)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* City and State */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          City *
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          State *
                        </label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    {/* Pincode */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pincode *
                      </label>
                      <input
                        type="text"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleInputChange}
                        placeholder="6-digit pincode"
                        required
                        maxLength="6"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Landmark */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Landmark
                      </label>
                      <input
                        type="text"
                        name="landmark"
                        value={formData.landmark}
                        onChange={handleInputChange}
                        placeholder="Nearby landmark (Optional)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Default Address */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_default"
                        checked={formData.is_default}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        Set as default address
                      </label>
                    </div>

                    {/* Buttons */}
                    <div className="flex space-x-4 pt-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
                          submitting
                            ? 'bg-gray-400 text-white cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {submitting ? 'Saving...' : (editingAddress ? 'Update' : 'Save')}
                      </button>
                      <button
                        type="button"
                        onClick={resetForm}
                        className="flex-1 py-2 px-4 bg-gray-200 text-gray-800 rounded-md font-medium hover:bg-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm p-6">
                  <Skeleton height={24} width={120} className="mb-4" />
                  <Skeleton height={16} className="mb-2" />
                  <Skeleton height={16} className="mb-2" />
                  <Skeleton height={16} />
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && (!addresses || addresses.length === 0) && (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm">
              <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No addresses found
              </h3>
              <p className="text-gray-500 mb-6">
                Add your first delivery address to get started
              </p>
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span>Add Address</span>
              </button>
            </div>
          )}

          {/* Addresses Grid */}
          {!loading && addresses && addresses.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {addresses.map((address) => (
                <div key={address.id} className="bg-white rounded-lg shadow-sm p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      {getAddressIcon(address.title || address.address_type)}
                      <h3 className="font-semibold text-gray-900">{address.title}</h3>
                      {address.is_default && (
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(address)}
                        className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(address.id)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="text-gray-600 space-y-1">
                    <p>{address.address_line_1}</p>
                    {address.address_line_2 && <p>{address.address_line_2}</p>}
                    <p>{address.city}, {address.state} {address.pincode}</p>
                    {address.landmark && (
                      <p className="text-sm text-gray-500">
                        Near {address.landmark}
                      </p>
                    )}
                  </div>

                  {/* Default Badge */}
                  {address.is_default && (
                    <div className="mt-4 inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                      Default Address
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}